/**
 * What a cook reads when something fails.
 *
 * `err.message` is never that. It is an English developer string — "Household
 * hh_czys… does not exist", "Server returned 500", Firebase's "Firebase: Error
 * (auth/unauthorized-domain)" — written for a log, sometimes with an id in it,
 * and never translated. On 2026-10-04 a French cook read one of those five
 * times. Every screen goes through here instead: the error's *code* picks a
 * sentence in the cook's language, and when nothing is known about the error
 * the screen's own fallback is used. `noRawErrorMessages.test.ts` fails on a
 * `.message` that reaches a display call.
 */
import { HouseholdError } from '../services/householdService';
import { isNetworkAuthError, isOfflineFetchError } from '../utils/authErrors';

type Translate = (key: string) => string;

/** One sentence per HouseholdError code; a missing entry means the fallback. */
const HOUSEHOLD_KEYS: Partial<Record<HouseholdError['code'], string>> = {
  'invite-not-found': 'join.errNotFound',
  'invite-expired': 'join.errExpired',
  'invite-already-consumed': 'join.errUsed',
  'household-full': 'household.errFull',
  'already-in-household': 'household.errAlreadyPaired',
  'not-in-household': 'household.errNotInHousehold',
  'migration-failed': 'household.errMoveFailed',
  network: 'household.errNetwork',
  'auth-required': 'household.errSessionExpired',
  'session-changed': 'household.errSessionChanged',
  'firebase-disabled': 'errors.serverError',
};

const codeOf = (err: unknown): string | undefined =>
  err && typeof err === 'object' ? (err as { code?: unknown }).code?.toString() : undefined;

const statusOf = (err: unknown): number | undefined =>
  err && typeof err === 'object' && typeof (err as { status?: unknown }).status === 'number'
    ? (err as { status: number }).status
    : undefined;

/**
 * The translation key for an error, or null when it has nothing a cook can
 * act on and the screen's fallback should speak.
 */
export const cookFacingErrorKey = (err: unknown): string | null => {
  // Offline first: an ApiError wrapped by toHouseholdError still carries the
  // browser's "Load failed", and that is the only thing worth saying.
  if (isOfflineFetchError(err) || isNetworkAuthError(err)) return 'errors.networkError';

  // `unknown` and `not-authorized` have no sentence: the screen's fallback speaks.
  if (err instanceof HouseholdError) return HOUSEHOLD_KEYS[err.code] ?? null;

  const code = codeOf(err);
  const status = statusOf(err);
  // The same test as aiService.isAuthRequiredError, kept local so the copy
  // layer does not depend on the AI client.
  if (code === 'auth-required' || status === 401) return 'errors.signedOut';
  if (status !== undefined && status >= 500) return 'errors.serverError';
  return null;
};

/**
 * The sentence to show for `err`, in the cook's language. `fallbackKey` is the
 * screen's own wording for "this did not work" and is what a cook sees for an
 * error nobody has given a better sentence to.
 */
export const cookFacingError = (err: unknown, t: Translate, fallbackKey: string): string =>
  t(cookFacingErrorKey(err) ?? fallbackKey);
